import * as React from 'react';
import { ThemeProvider, createTheme, alpha, Theme } from '@mui/material/styles';
import type { ThemeOptions } from '@mui/material/styles';
import { inputsCustomizations } from './customizations/inputs';
import { dataDisplayCustomizations } from './customizations/dataDisplay';
import { feedbackCustomizations } from './customizations/feedback';
import { navigationCustomizations } from './customizations/navigation';
import { surfacesCustomizations } from './customizations/surfaces';
import { colorSchemes, colorSchemesPastel, colorSchemesEarth, colorSchemesSage, colorSchemesOcean, colorSchemesWhite, colorSchemesBlack, colorSchemesSunset, colorSchemesForest, colorSchemesMidnight, colorSchemesAurora, colorSchemesAmber, colorSchemesEmerald, colorSchemesPremium, colorSchemesMuiOfficial, colorSchemesSlateBluePlus, colorSchemesWarmSuccess, colorSchemesMidnightLuxe, colorSchemesAuroraFresh, colorSchemesNeoTokyo, colorSchemesSakura, colorSchemesMonochromePro, colorSchemesTerracotta, colorSchemesNordicFrost, warmSuccessPrimary, warmSuccessNeutral, typography, shadows, shape, brand, gray, green, orange, red } from './themePrimitives';
import { ThemeVariantProvider, useThemeVariant } from '../contexts/ThemeVariantContext';
import { AccentColorModeProvider, useAccentColorMode } from '../contexts/AccentColorModeContext';
import { useColorScheme } from '@mui/material/styles';
import { svgIconClasses } from '@mui/material/SvgIcon';
import { chipClasses } from '@mui/material/Chip';
import { getCustomThemeById, CustomTheme } from '../services/themeManagementService';
import { UIElementColorMapping } from '../components/admin/appearance/ThemeEditor';

interface AppThemeProps {
  children: React.ReactNode;
  disableCustomTheme?: boolean;
  themeComponents?: ThemeOptions['components'];
}

// Define which themes are inherently dark
const DARK_THEMES = ['black', 'premium', 'midnight', 'glass', 'midnight-luxe', 'neo-tokyo', 'monochrome-pro'];

// Inner component that can access useColorScheme (must be inside ThemeProvider)
// Now syncs color mode from global admin configuration instead of localStorage
function ThemeModeSyncer({ variant, globalColorMode }: { variant: string; globalColorMode: 'light' | 'dark' }) {
  const { mode, setMode } = useColorScheme();

  // Sync color mode from global admin configuration
  React.useEffect(() => {
    if (variant.startsWith('custom-')) return; // Skip for custom themes

    // Use global color mode from Firestore admin configuration
    // Dark themes override to dark mode regardless of global setting
    const isDarkTheme = DARK_THEMES.includes(variant);
    const expectedMode = isDarkTheme ? 'dark' : globalColorMode;

    // Sync React state
    if (mode !== expectedMode) {
      setMode(expectedMode);
    }

    // Ensure DOM attribute is set correctly (no localStorage needed)
    const currentAttr = document.documentElement.getAttribute('data-mui-color-scheme');
    if (currentAttr !== expectedMode) {
      document.documentElement.setAttribute('data-mui-color-scheme', expectedMode);
    }
  }, [variant, globalColorMode, mode, setMode]);

  return null;
}

function AppThemeContent(props: AppThemeProps) {
  const { children, disableCustomTheme, themeComponents } = props;
  const { variant, colorMode, loading: themeLoading } = useThemeVariant();
  const { accentColorMode } = useAccentColorMode();
  const [customTheme, setCustomTheme] = React.useState<CustomTheme | null>(null);
  const [loadingCustomTheme, setLoadingCustomTheme] = React.useState(false);

  // Debug: Log when variant changes
  React.useEffect(() => {
    // Log('[AppThemeContent] Received variant:', variant, 'colorMode:', colorMode);
  }, [variant, colorMode]);

  // Note: Theme is now controlled globally by admin via Firestore appConfiguration
  // No user profile sync needed - theme comes from useGlobalTheme hook via context

  // Load custom theme if variant starts with "custom-"
  React.useEffect(() => {
    const loadCustomTheme = async () => {
      if (variant && variant.startsWith('custom-')) {
        const themeId = variant.replace('custom-', '');
        setLoadingCustomTheme(true);
        try {
          const theme = await getCustomThemeById(themeId);
          setCustomTheme(theme);
        } catch (error) {
          // Error('Error loading custom theme:', error);
          setCustomTheme(null);
        } finally {
          setLoadingCustomTheme(false);
        }
      } else {
        setCustomTheme(null);
      }
    };
    loadCustomTheme();
  }, [variant]);

  const theme = React.useMemo(() => {
    if (disableCustomTheme) return {};

    // If custom theme is loaded, apply it exactly as configured
    if (customTheme && !loadingCustomTheme) {
      const palette = customTheme.palette || {};
      const primaryColor = palette[500] || customTheme.baseColor || '#3498db';
      const uiColors: UIElementColorMapping = customTheme.uiElementColors || {};

      // Helper function to get UI color or fallback to palette
      const getUIColor = (key: keyof UIElementColorMapping, fallback: string) => {
        return uiColors[key] || fallback;
      };

      return createTheme({
        typography: {
          fontFamily: customTheme.fontFamily || typography.fontFamily,
        },
        palette: {
          mode: 'light', // Force light mode for custom themes to preserve colors
          primary: {
            main: getUIColor('buttonPrimary', primaryColor),
            light: palette[300] || primaryColor,
            dark: getUIColor('buttonHover', palette[700] || primaryColor),
            contrastText: getUIColor('buttonText', '#ffffff'),
          },
          secondary: {
            main: getUIColor('buttonSecondary', palette[400] || primaryColor),
            light: palette[200] || primaryColor,
            dark: palette[600] || primaryColor,
          },
          background: {
            default: customTheme.backgroundColor || '#ffffff',
            paper: getUIColor('cardBackground', customTheme.backgroundPaper || customTheme.backgroundColor || '#ffffff'),
          },
          text: {
            primary: customTheme.textColor || '#212121',
            secondary: alpha(customTheme.textColor || '#212121', 0.7),
          },
          divider: getUIColor('dividerColor', alpha('#000000', 0.12)),
          error: {
            main: '#f44336',
            light: '#e57373',
            dark: '#d32f2f',
          },
          warning: {
            main: '#ff9800',
            light: '#ffb74d',
            dark: '#f57c00',
          },
          info: {
            main: primaryColor,
            light: palette[300] || primaryColor,
            dark: palette[700] || primaryColor,
          },
          success: {
            main: '#4caf50',
            light: '#81c784',
            dark: '#388e3c',
          },
        },
        components: {
          ...inputsCustomizations,
          ...dataDisplayCustomizations,
          ...feedbackCustomizations,
          ...navigationCustomizations,
          ...surfacesCustomizations,
          ...themeComponents,
          MuiCssBaseline: {
            styleOverrides: {
              body: {
                backgroundColor: customTheme.backgroundColor || '#ffffff',
                color: customTheme.textColor || '#212121',
                fontFamily: customTheme.fontFamily || typography.fontFamily,
                // Variables CSS personnalisées pour les éléments UI
                // Scores
                '--fitd-score-background': getUIColor('scoreBackground', palette[50] || '#f5f5f5'),
                '--fitd-score-text': getUIColor('scoreText', customTheme.textColor || '#212121'),
                '--fitd-score-average': getUIColor('scoreAverage', primaryColor),
                '--fitd-score-high': getUIColor('scoreHigh', palette[600] || primaryColor),
                '--fitd-score-low': getUIColor('scoreLow', palette[400] || primaryColor),
                // Charts/Graphiques
                '--fitd-chart-background': getUIColor('chartBackground', customTheme.backgroundPaper || '#ffffff'),
                '--fitd-chart-border': getUIColor('chartBorder', palette[200] || '#e0e0e0'),
                '--fitd-chart-gridlines': getUIColor('chartGridLines', palette[200] || '#e0e0e0'),
                '--fitd-chart-primary': getUIColor('chartPrimary', primaryColor),
                '--fitd-chart-secondary': getUIColor('chartSecondary', palette[300] || primaryColor),
                '--fitd-chart-tertiary': getUIColor('chartTertiary', palette[700] || primaryColor),
                '--fitd-chart-axis-text': getUIColor('chartAxisText', alpha(customTheme.textColor || '#000000', 0.7)),
                // Gradients
                '--fitd-gradient-start': getUIColor('gradientStart', palette[300] || primaryColor),
                '--fitd-gradient-end': getUIColor('gradientEnd', palette[700] || primaryColor),
                // Links
                '--fitd-link-color': getUIColor('linkColor', primaryColor),
                '--fitd-link-hover': getUIColor('linkHoverColor', palette[700] || primaryColor),
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundColor: getUIColor('cardBackground', customTheme.backgroundPaper || customTheme.backgroundColor || '#ffffff'),
                color: customTheme.textColor || '#212121',
                borderColor: getUIColor('cardBorder', alpha('#000000', 0.12)),
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                backgroundColor: getUIColor('cardBackground', customTheme.backgroundPaper || '#ffffff'),
                borderColor: getUIColor('cardBorder', alpha('#000000', 0.12)),
                boxShadow: `0 2px 8px ${getUIColor('cardShadow', alpha(customTheme.textColor || '#000000', 0.1))}`,
                '& .MuiCardHeader-root': {
                  backgroundColor: getUIColor('cardHeader', 'transparent'),
                },
              },
            },
          },
          MuiAppBar: {
            styleOverrides: {
              root: {
                backgroundColor: getUIColor('navbarBackground', customTheme.backgroundColor || '#ffffff'),
                color: getUIColor('navbarText', customTheme.textColor || '#212121'),
                borderBottom: `1px solid ${getUIColor('navbarBorder', alpha('#000000', 0.12))}`,
                '& .MuiIconButton-root': {
                  color: getUIColor('navbarIconColor', primaryColor),
                },
              },
            },
          },
          MuiTextField: {
            styleOverrides: {
              root: {
                '& .MuiOutlinedInput-root': {
                  backgroundColor: getUIColor('inputBackground', customTheme.backgroundPaper || '#ffffff'),
                  '& fieldset': {
                    borderColor: getUIColor('inputBorder', alpha('#000000', 0.23)),
                  },
                  '&:hover fieldset': {
                    borderColor: getUIColor('inputBorder', alpha('#000000', 0.4)),
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: getUIColor('inputFocusBorder', primaryColor),
                  },
                  '& input': {
                    color: getUIColor('inputText', customTheme.textColor || '#212121'),
                  },
                  '& input::placeholder': {
                    color: getUIColor('inputPlaceholder', alpha(customTheme.textColor || '#000000', 0.5)),
                    opacity: 1,
                  },
                },
                '& .MuiInputLabel-root': {
                  color: getUIColor('inputLabel', alpha(customTheme.textColor || '#000000', 0.7)),
                },
              },
            },
          },
          MuiAlert: {
            styleOverrides: {
              standardSuccess: {
                color: getUIColor('alertSuccess', '#4caf50'),
                backgroundColor: getUIColor('alertSuccessBackground', alpha('#4caf50', 0.1)),
              },
              standardError: {
                color: getUIColor('alertError', '#f44336'),
                backgroundColor: getUIColor('alertErrorBackground', alpha('#f44336', 0.1)),
              },
              standardWarning: {
                color: getUIColor('alertWarning', '#ff9800'),
                backgroundColor: getUIColor('alertWarningBackground', alpha('#ff9800', 0.1)),
              },
              standardInfo: {
                color: getUIColor('alertInfo', primaryColor),
                backgroundColor: getUIColor('alertInfoBackground', alpha(primaryColor, 0.1)),
              },
            },
          },
          MuiTableHead: {
            styleOverrides: {
              root: {
                backgroundColor: getUIColor('tableHeaderBackground', palette[100] || '#f5f5f5'),
                '& .MuiTableCell-root': {
                  color: getUIColor('tableHeaderText', customTheme.textColor || '#212121'),
                  borderBottom: `1px solid ${getUIColor('tableBorder', alpha('#000000', 0.12))}`,
                },
              },
            },
          },
          MuiTableRow: {
            styleOverrides: {
              root: {
                backgroundColor: getUIColor('tableRowBackground', customTheme.backgroundPaper || '#ffffff'),
                '&:nth-of-type(even)': {
                  backgroundColor: getUIColor('tableRowAlternate', palette[50] || '#fafafa'),
                },
                '&:hover': {
                  backgroundColor: getUIColor('tableRowHover', palette[100] || '#f5f5f5'),
                },
              },
            },
          },
          MuiMenu: {
            styleOverrides: {
              paper: {
                backgroundColor: getUIColor('menuBackground', customTheme.backgroundPaper || '#ffffff'),
                border: `1px solid ${getUIColor('menuBorder', alpha('#000000', 0.12))}`,
                '& .MuiMenuItem-root': {
                  color: getUIColor('menuText', customTheme.textColor || '#212121'),
                  '&:hover': {
                    backgroundColor: getUIColor('menuHover', palette[100] || '#f5f5f5'),
                  },
                  '&.Mui-selected': {
                    backgroundColor: getUIColor('menuItemActive', palette[200] || '#e0e0e0'),
                  },
                },
              },
            },
          },
          MuiTooltip: {
            styleOverrides: {
              tooltip: {
                backgroundColor: getUIColor('tooltipBackground', palette[800] || '#424242'),
                color: getUIColor('tooltipText', '#ffffff'),
                border: `1px solid ${getUIColor('tooltipBorder', palette[700] || '#616161')}`,
              },
            },
          },
          MuiDialog: {
            styleOverrides: {
              paper: {
                backgroundColor: getUIColor('modalBackground', customTheme.backgroundPaper || '#ffffff'),
                border: `1px solid ${getUIColor('modalBorder', alpha('#000000', 0.12))}`,
                boxShadow: `0 8px 32px ${getUIColor('modalShadow', alpha(customTheme.textColor || '#000000', 0.2))}`,
              },
            },
          },
          MuiBadge: {
            styleOverrides: {
              badge: {
                backgroundColor: getUIColor('badgeBackground', primaryColor),
                color: getUIColor('badgeText', '#ffffff'),
                border: `1px solid ${getUIColor('badgeBorder', palette[600] || primaryColor)}`,
              },
            },
          },
          MuiTabs: {
            styleOverrides: {
              root: {
                backgroundColor: getUIColor('tabBackground', customTheme.backgroundColor || '#ffffff'),
                borderBottom: `1px solid ${getUIColor('tabBorder', alpha('#000000', 0.12))}`,
              },
            },
          },
          MuiTab: {
            styleOverrides: {
              root: {
                color: getUIColor('tabText', alpha(customTheme.textColor || '#000000', 0.7)),
                '&.Mui-selected': {
                  backgroundColor: getUIColor('tabActiveBackground', palette[100] || '#f5f5f5'),
                  color: getUIColor('tabActiveText', primaryColor),
                },
              },
            },
          },
          MuiAccordion: {
            styleOverrides: {
              root: {
                backgroundColor: getUIColor('accordionBackground', customTheme.backgroundPaper || '#ffffff'),
                border: `1px solid ${getUIColor('accordionBorder', alpha('#000000', 0.12))}`,
                '& .MuiAccordionSummary-root': {
                  backgroundColor: getUIColor('accordionHeaderBackground', palette[50] || '#fafafa'),
                  color: getUIColor('accordionHeaderText', customTheme.textColor || '#212121'),
                },
              },
            },
          },
          MuiButton: {
            styleOverrides: {
              root: {
                '&.MuiButton-containedPrimary': {
                  backgroundColor: getUIColor('buttonPrimary', primaryColor),
                  color: getUIColor('buttonText', '#ffffff'),
                  '&:hover': {
                    backgroundColor: getUIColor('buttonHover', palette[600] || primaryColor),
                  },
                },
                '&.MuiButton-containedSecondary': {
                  backgroundColor: getUIColor('buttonSecondary', palette[400] || primaryColor),
                  '&:hover': {
                    backgroundColor: palette[500] || primaryColor,
                  },
                },
                '&.MuiButton-textPrimary': {
                  color: getUIColor('buttonPrimary', primaryColor),
                  '&:hover': {
                    backgroundColor: alpha(getUIColor('buttonPrimary', primaryColor), 0.1),
                  },
                },
              },
            },
          },
          MuiDrawer: {
            styleOverrides: {
              paper: {
                backgroundColor: getUIColor('sidebarBackground', palette[50] || '#f5f5f5'),
                color: getUIColor('sidebarText', customTheme.textColor || '#212121'),
                borderRight: `1px solid ${getUIColor('sidebarBorder', alpha('#000000', 0.12))}`,
                '& .MuiListItem-root': {
                  color: getUIColor('sidebarText', customTheme.textColor || '#212121'),
                  '&:hover': {
                    backgroundColor: getUIColor('sidebarHover', alpha(primaryColor, 0.1)),
                  },
                  '&.Mui-selected': {
                    backgroundColor: getUIColor('sidebarActive', alpha(primaryColor, 0.15)),
                    color: getUIColor('buttonPrimary', primaryColor),
                  },
                },
              },
            },
          },
          MuiLink: {
            styleOverrides: {
              root: {
                color: getUIColor('linkColor', primaryColor),
                '&:hover': {
                  color: getUIColor('buttonHover', palette[600] || primaryColor),
                },
              },
            },
          },
          MuiChip: {
            styleOverrides: {
              root: {
                '&.MuiChip-colorDefault': {
                  backgroundColor: getUIColor('chipBackground', alpha(primaryColor, 0.1)),
                  color: getUIColor('chipText', palette[700] || '#212121'),
                },
              },
            },
          },
          MuiLinearProgress: {
            styleOverrides: {
              bar: {
                backgroundColor: getUIColor('progressBar', primaryColor),
              },
            },
          },
          MuiSvgIcon: {
            styleOverrides: {
              root: {
                color: customTheme.iconColor || (customTheme.iconSet !== 'emoji' && customTheme.iconSet !== 'none' ? '#757575' : undefined),
              },
            },
          },
        },
        shadows,
        shape,
      });
    }

    const glassOverrides: ThemeOptions['components'] = variant === 'glass' ? {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundImage: 'linear-gradient(135deg, #121212 0%, #000000 100%)',
            backgroundAttachment: 'fixed',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: 'rgba(18, 18, 18, 0.6)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: 'rgba(18, 18, 18, 0.6)',
            backdropFilter: 'blur(12px)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: 'rgba(18, 18, 18, 0.6)',
            backdropFilter: 'blur(12px)',
            borderRight: '1px solid rgba(255, 255, 255, 0.08)',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: 'rgba(255, 255, 255, 0.03)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.05)',
          },
        },
      },
    } : {};

    // Customizations pour thèmes monochromatiques (earth, sage, ocean, white, black, sunset, forest, midnight, aurora, amber, emerald, mui-official)
    // Force l'utilisation uniquement des couleurs de la palette pour icônes, surlignages, etc.
    const monochromeOverrides: ThemeOptions['components'] =
      (variant === 'earth' || variant === 'sage' || variant === 'ocean' || variant === 'white' || variant === 'black' || variant === 'sunset' || variant === 'forest' || variant === 'midnight' || variant === 'aurora' || variant === 'amber' || variant === 'emerald' || variant === 'mui-official' || variant === 'slate-blue' || variant === 'warm-success' || variant === 'midnight-luxe' || variant === 'aurora-fresh' || variant === 'neo-tokyo' || variant === 'sakura' || variant === 'monochrome-pro' || variant === 'terracotta' || variant === 'nordic-frost') ? {
        MuiSvgIcon: {
          styleOverrides: {
            root: ({ theme }) => ({
              color: theme.palette.text.secondary,
              '&.MuiChip-icon': {
                color: theme.palette.primary.main,
              },
            }),
          },
        },
        MuiChip: {
          styleOverrides: {
            root: ({ theme }) => ({
              '&.MuiChip-colorDefault': {
                borderColor: theme.palette.divider,
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                [`& .${chipClasses.label}`]: {
                  color: theme.palette.text.primary,
                },
                [`& .${chipClasses.icon}`]: {
                  color: theme.palette.primary.main,
                },
              },
              '&.MuiChip-colorSuccess': {
                borderColor: theme.palette.success.main,
                backgroundColor: alpha(theme.palette.success.main, 0.1),
                [`& .${chipClasses.label}`]: {
                  color: theme.palette.success.main,
                },
                [`& .${chipClasses.icon}`]: {
                  color: theme.palette.success.main,
                },
              },
              '&.MuiChip-colorError': {
                borderColor: theme.palette.error.main,
                backgroundColor: alpha(theme.palette.error.main, 0.1),
                [`& .${chipClasses.label}`]: {
                  color: theme.palette.error.main,
                },
                [`& .${chipClasses.icon}`]: {
                  color: theme.palette.error.main,
                },
              },
              '&.MuiChip-colorWarning': {
                borderColor: theme.palette.warning.main,
                backgroundColor: alpha(theme.palette.warning.main, 0.1),
                [`& .${chipClasses.label}`]: {
                  color: theme.palette.warning.main,
                },
                [`& .${chipClasses.icon}`]: {
                  color: theme.palette.warning.main,
                },
              },
              '&.MuiChip-colorInfo': {
                borderColor: theme.palette.info.main,
                backgroundColor: alpha(theme.palette.info.main, 0.1),
                [`& .${chipClasses.label}`]: {
                  color: theme.palette.info.main,
                },
                [`& .${chipClasses.icon}`]: {
                  color: theme.palette.info.main,
                },
              },
            }),
          },
        },
        MuiListItem: {
          styleOverrides: {
            root: ({ theme }) => ({
              [`& .${svgIconClasses.root}`]: {
                color: theme.palette.text.secondary,
              },
              '&.Mui-selected': {
                [`& .${svgIconClasses.root}`]: {
                  color: theme.palette.primary.main,
                },
              },
            }),
          },
        },
        MuiIconButton: {
          styleOverrides: {
            root: ({ theme }) => ({
              color: theme.palette.text.primary,
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
              },
            }),
          },
        },
        MuiButton: {
          styleOverrides: {
            root: ({ theme }) => ({
              '&.MuiButton-textPrimary': {
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                },
              },
              '&.MuiButton-textSecondary': {
                color: theme.palette.secondary.main,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                },
              },
            }),
          },
        },
        MuiLink: {
          styleOverrides: {
            root: ({ theme }) => ({
              color: theme.palette.primary.main,
              '&:hover': {
                color: theme.palette.primary.dark,
              },
            }),
          },
        },
        MuiCssBaseline: {
          styleOverrides: {
            '::selection': ({ theme }: { theme: Theme }) => ({
              backgroundColor: alpha(theme.palette.primary.main, 0.2),
              color: theme.palette.text.primary,
            }),
            // Modern browsers (including Firefox 62+) support ::selection without vendor prefix
            // Removed -moz-selection as it's no longer needed and causes Emotion parsing errors
          },
        },
      } : {};

    // Slate Blue+ overrides (Button/Card/Chip/Paper/TextField + CSS variables)
    const slateBlueOverrides: ThemeOptions['components'] = variant === 'slate-blue' ? {
      MuiCssBaseline: {
        styleOverrides: {
          '::selection': ({ theme }: { theme: Theme }) => ({
            backgroundColor: alpha(theme.palette.primary.main, 0.2),
            color: theme.palette.text.primary,
          }),
          ':root': {
            '--fitd-bg-primary': '#F1F5F9',
            '--fitd-bg-card': '#FFFFFF',
            '--fitd-border': '#E2E8F0',
            '--fitd-button-primary': '#3B5998',
            '--fitd-button-hover': '#4C70BA',
            '--fitd-button-text': '#FFFFFF',
            '--fitd-text-primary': '#0F172A',
            '--fitd-text-secondary': '#64748B',
            '--fitd-shadow': '0 1px 3px 0 rgba(0, 0, 0, 0.05)',
            '--fitd-shadow-hover': '0 4px 12px 0 rgba(0, 0, 0, 0.08)',
          },
          ':root[data-mui-color-scheme="dark"]': {
            '--fitd-bg-primary': '#0F172A',
            '--fitd-bg-card': '#1E293B',
            '--fitd-border': '#334155',
            '--fitd-button-primary': '#FFFFFF',
            '--fitd-button-hover': '#F1F5F9',
            '--fitd-button-text': '#3B5998',
            '--fitd-text-primary': '#F1F5F9',
            '--fitd-text-secondary': '#94A3B8',
            '--fitd-shadow': 'none',
            '--fitd-shadow-hover': 'none',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            textTransform: 'none',
            fontWeight: 500,
            borderRadius: '4px',
            padding: '12px 24px',
            boxShadow: 'none',
            transition: 'all 200ms ease-in-out',
            '&:hover': {
              boxShadow: 'none',
            },
            '&.MuiButton-contained': {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              '&:hover': {
                backgroundColor: theme.palette.primary.light,
              },
            },
            '&.MuiButton-outlined': {
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
              borderWidth: '2px',
              '&:hover': {
                borderWidth: '2px',
                backgroundColor: alpha(theme.palette.primary.main, 0.04),
              },
            },
            '&.MuiButton-text': {
              color: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.04),
              },
            },
          }),
        },
      },
      MuiCard: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: '8px',
            border: '1px solid',
            borderColor: theme.palette.divider,
            boxShadow: 'var(--fitd-shadow)',
            padding: '20px',
            transition: 'all 200ms ease-in-out',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: 'var(--fitd-shadow-hover)',
            },
          }),
        },
      },
      MuiChip: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: '4px',
            fontWeight: 500,
            fontSize: '0.8125rem',
            ...(theme.palette.mode === 'dark' && {
              backgroundColor: theme.palette.divider,
              color: theme.palette.text.secondary,
            }),
          }),
          outlined: ({ theme }) => ({
            borderColor: theme.palette.divider,
            color: theme.palette.text.secondary,
          }),
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            borderRadius: '8px',
          },
          elevation1: {
            boxShadow: 'var(--fitd-shadow)',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: ({ theme }) => ({
            '& .MuiOutlinedInput-root': {
              borderRadius: '4px',
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
            },
          }),
        },
      },
    } : {};

    // Warm Success overrides (Button/Card/Chip/Paper/TextField + CSS variables)
    const warmSuccessOverrides: ThemeOptions['components'] = variant === 'warm-success' ? {
      MuiCssBaseline: {
        styleOverrides: {
          '::selection': ({ theme }: { theme: Theme }) => ({
            backgroundColor: alpha(theme.palette.primary.main, 0.15),
            color: theme.palette.text.primary,
          }),
          ':root': {
            '--fitd-bg-primary': '#FFFBF5',
            '--fitd-bg-card': '#FFFFFF',
            '--fitd-bg-elevated': '#FFFAF3',
            '--fitd-border': '#E7E5E4',
            '--fitd-button-primary': '#166534',
            '--fitd-button-hover': '#16A34A',
            '--fitd-button-text': '#FFFFFF',
            '--fitd-text-primary': '#1C1917',
            '--fitd-text-secondary': '#57534E',
            '--fitd-text-muted': '#78716C',
            '--fitd-accent': '#EA580C',
            '--fitd-success': '#22C55E',
            '--fitd-warning': '#F59E0B',
            '--fitd-shadow': '0 4px 6px -1px rgba(28, 25, 23, 0.07), 0 2px 4px -2px rgba(28, 25, 23, 0.05)',
            '--fitd-shadow-hover': '0 10px 15px -3px rgba(28, 25, 23, 0.08), 0 4px 6px -4px rgba(28, 25, 23, 0.05)',
            '--fitd-shadow-primary': '0 8px 24px rgba(22, 101, 52, 0.25)',
            '--fitd-focus-ring': '0 0 0 3px rgba(34, 197, 94, 0.4)',
          },
          ':root[data-mui-color-scheme="dark"]': {
            '--fitd-bg-primary': '#1C1917',
            '--fitd-bg-card': '#292524',
            '--fitd-bg-elevated': '#292524',
            '--fitd-border': '#44403C',
            '--fitd-button-primary': '#22C55E',
            '--fitd-button-hover': '#4ADE80',
            '--fitd-button-text': '#FFFFFF',
            '--fitd-text-primary': '#FAFAF9',
            '--fitd-text-secondary': '#A8A29E',
            '--fitd-text-muted': '#78716C',
            '--fitd-accent': '#FB923C',
            '--fitd-success': '#4ADE80',
            '--fitd-warning': '#FBBF24',
            '--fitd-shadow': 'none',
            '--fitd-shadow-hover': 'none',
            '--fitd-shadow-primary': '0 8px 24px rgba(34, 197, 94, 0.15)',
            '--fitd-focus-ring': '0 0 0 3px rgba(74, 222, 128, 0.3)',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: '8px',
            padding: '12px 24px',
            boxShadow: 'none',
            transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
            },
            '&:active': {
              transform: 'translateY(0)',
            },
            '&.MuiButton-contained': {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              boxShadow: 'var(--fitd-shadow-primary)',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.9),
                boxShadow: '0 12px 32px rgba(22, 101, 52, 0.35)',
              },
              // Callers set a custom bgcolor via sx (e.g. warmSuccessPrimary[700]) that
              // keeps the colored background through the disabled state, but MUI's own
              // Mui-disabled rule dims the text to a barely-visible grey regardless —
              // force it back to white so disabled contained buttons stay readable.
              '&.Mui-disabled': {
                color: '#FFFFFF',
              },
            },
            '&.MuiButton-outlined': {
              borderColor: theme.palette.divider,
              color: theme.palette.text.primary,
              '&:hover': {
                borderColor: theme.palette.primary.main,
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
              },
            },
            '&.MuiButton-text': {
              color: theme.palette.text.secondary,
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
                color: theme.palette.text.primary,
              },
            },
          }),
        },
      },
      MuiCard: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: '12px',
            border: '1px solid',
            borderColor: theme.palette.divider,
            boxShadow: 'none',
            padding: '24px',
            transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: 'var(--fitd-shadow-hover)',
            },
          }),
        },
      },
      MuiChip: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: '9999px',
            fontWeight: 500,
            fontSize: '0.875rem',
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.main,
          }),
          outlined: ({ theme }) => ({
            borderColor: theme.palette.divider,
            color: theme.palette.text.secondary,
            backgroundColor: 'transparent',
          }),
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            borderRadius: '12px',
          },
          elevation1: {
            boxShadow: 'var(--fitd-shadow)',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: ({ theme }) => ({
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              '& fieldset': {
                borderColor: theme.palette.divider,
              },
              '&:hover fieldset': {
                borderColor: alpha(theme.palette.text.primary, 0.3),
              },
              '&.Mui-focused fieldset': {
                borderColor: theme.palette.primary.main,
              },
            },
            '& .MuiInputBase-input': {
              '&::placeholder': {
                color: theme.palette.text.secondary,
                opacity: 0.7,
              },
            },
          }),
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: '12px !important',
            border: '1px solid',
            borderColor: theme.palette.divider,
            boxShadow: 'none',
            '&:before': {
              display: 'none',
            },
            '&.Mui-expanded': {
              margin: 0,
              boxShadow: 'var(--fitd-shadow)',
            },
          }),
        },
      },
    } : {};

    // Sélectionner le colorScheme selon le variant
    let selectedColorSchemes;
    if (variant === 'pastel') {
      selectedColorSchemes = colorSchemesPastel;
    } else if (variant === 'earth') {
      selectedColorSchemes = colorSchemesEarth;
    } else if (variant === 'sage') {
      selectedColorSchemes = colorSchemesSage;
    } else if (variant === 'ocean') {
      selectedColorSchemes = colorSchemesOcean;
    } else if (variant === 'white') {
      selectedColorSchemes = colorSchemesWhite;
    } else if (variant === 'black') {
      selectedColorSchemes = colorSchemesBlack;
    } else if (variant === 'sunset') {
      selectedColorSchemes = colorSchemesSunset;
    } else if (variant === 'forest') {
      selectedColorSchemes = colorSchemesForest;
    } else if (variant === 'midnight') {
      selectedColorSchemes = colorSchemesMidnight;
    } else if (variant === 'aurora') {
      selectedColorSchemes = colorSchemesAurora;
    } else if (variant === 'amber') {
      selectedColorSchemes = colorSchemesAmber;
    } else if (variant === 'emerald') {
      selectedColorSchemes = colorSchemesEmerald;
    } else if (variant === 'premium') {
      selectedColorSchemes = colorSchemesPremium;
    } else if (variant === 'slate-blue') {
      selectedColorSchemes = colorSchemesSlateBluePlus;
    } else if (variant === 'mui-official') {
      selectedColorSchemes = colorSchemesMuiOfficial;
    } else if (variant === 'warm-success') {
      selectedColorSchemes = colorSchemesWarmSuccess;
    } else if (variant === 'midnight-luxe') {
      selectedColorSchemes = colorSchemesMidnightLuxe;
    } else if (variant === 'aurora-fresh') {
      selectedColorSchemes = colorSchemesAuroraFresh;
    } else if (variant === 'neo-tokyo') {
      selectedColorSchemes = colorSchemesNeoTokyo;
    } else if (variant === 'sakura') {
      selectedColorSchemes = colorSchemesSakura;
    } else if (variant === 'monochrome-pro') {
      selectedColorSchemes = colorSchemesMonochromePro;
    } else if (variant === 'terracotta') {
      selectedColorSchemes = colorSchemesTerracotta;
    } else if (variant === 'nordic-frost') {
      selectedColorSchemes = colorSchemesNordicFrost;
    } else {
      selectedColorSchemes = colorSchemes;
    }

    // Pour le thème white, toujours utiliser les couleurs brand en accentuation
    // Si accentColorMode est 'default' OU si variant est 'white', remplacer les couleurs d'accentuation par les couleurs par défaut
    if ((accentColorMode === 'default' || variant === 'white') && variant !== 'slate-blue' && variant !== 'warm-success' && variant !== 'midnight-luxe' && variant !== 'aurora-fresh' && variant !== 'neo-tokyo' && variant !== 'sakura' && variant !== 'monochrome-pro' && variant !== 'terracotta' && variant !== 'nordic-frost' && selectedColorSchemes) {
      const defaultColorsLight = {
        primary: {
          light: brand[200],
          main: brand[400],
          dark: brand[700],
          contrastText: brand[50],
        },
        info: {
          light: brand[100],
          main: brand[300],
          dark: brand[600],
          contrastText: gray[50],
        },
        warning: {
          light: orange[300],
          main: orange[400],
          dark: orange[800],
        },
        error: {
          light: red[300],
          main: red[400],
          dark: red[800],
        },
        success: {
          light: green[300],
          main: green[400],
          dark: green[800],
        },
      };

      const defaultColorsDark = {
        primary: {
          contrastText: brand[50],
          light: brand[300],
          main: brand[400],
          dark: brand[700],
        },
        info: {
          contrastText: brand[300],
          light: brand[500],
          main: brand[700],
          dark: brand[900],
        },
        warning: {
          light: orange[400],
          main: orange[500],
          dark: orange[700],
        },
        error: {
          light: red[400],
          main: red[500],
          dark: red[700],
        },
        success: {
          light: green[400],
          main: green[500],
          dark: green[700],
        },
      };

      // Appliquer les couleurs par défaut tout en conservant les autres propriétés du thème
      selectedColorSchemes = {
        light: {
          ...selectedColorSchemes.light,
          palette: {
            ...selectedColorSchemes.light.palette,
            ...defaultColorsLight,
          },
        },
        dark: {
          ...selectedColorSchemes.dark,
          palette: {
            ...selectedColorSchemes.dark.palette,
            ...defaultColorsDark,
          },
        },
      };
    }

    return createTheme({
      cssVariables: {
        colorSchemeSelector: 'data-mui-color-scheme',
        cssVarPrefix: 'template',
      },
      colorSchemes: selectedColorSchemes,
      typography,
      shadows,
      shape: variant === 'slate-blue' ? { borderRadius: 4 } :
             variant === 'warm-success' ? { borderRadius: 8 } :
             variant === 'midnight-luxe' || variant === 'neo-tokyo' || variant === 'monochrome-pro' ? { borderRadius: 4 } :
             variant === 'aurora-fresh' || variant === 'nordic-frost' ? { borderRadius: 10 } :
             variant === 'sakura' || variant === 'terracotta' ? { borderRadius: 8 } : shape,
      components: {
        ...inputsCustomizations,
        ...dataDisplayCustomizations,
        ...feedbackCustomizations,
        ...navigationCustomizations,
        ...surfacesCustomizations,
        ...themeComponents,
        ...glassOverrides,
        ...monochromeOverrides,
        ...slateBlueOverrides,
        ...warmSuccessOverrides,
      },
    });
  }, [disableCustomTheme, themeComponents, variant, accentColorMode, customTheme, loadingCustomTheme]);

  // Debug: Log when theme object changes (must be before conditional return)
  React.useEffect(() => {
    // Log('[AppThemeContent] Theme object updated for variant:', variant);
    // Log('[AppThemeContent] Theme palette primary:', theme?.palette?.primary);
  }, [theme, variant]);

  if (disableCustomTheme) {
    return <React.Fragment>{children}</React.Fragment>;
  }

  // Key forces complete re-render when theme variant changes
  return (
    <ThemeProvider
      key={`theme-${variant}-${colorMode}`}
      theme={theme}
      defaultMode="light"
      disableTransitionOnChange
    >
      <ThemeModeSyncer variant={variant} globalColorMode={colorMode} />
      {children}
    </ThemeProvider>
  );
}

export default function AppTheme(props: AppThemeProps) {
  return (
    <ThemeVariantProvider>
      <AccentColorModeProvider>
        <AppThemeContent {...props} />
      </AccentColorModeProvider>
    </ThemeVariantProvider>
  );
}
